import { useState, useEffect } from 'react'
import api from '../../services/api'
import { Sparkles, CheckCircle2, AlertTriangle, AlertCircle, Copy, Check, RefreshCw, EyeOff } from 'lucide-react'

const STATUS_BADGES = {
  VERIFIED: {
    label: 'VERIFIED',
    className: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
    description: 'Concrete available evidence directly confirms completion',
  },
  APPARENT: {
    label: 'APPARENT',
    className: 'text-cyan-400 border-cyan-500/30 bg-cyan-500/10',
    description: 'Observable evidence suggests compliance; visual inspection required',
  },
  NOT_FOUND: {
    label: 'NOT FOUND',
    className: 'text-rose-400 border-rose-500/30 bg-rose-500/10',
    description: 'Explicitly required deliverable/evidence is absent',
  },
  UNVERIFIABLE: {
    label: 'UNVERIFIABLE',
    className: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
    description: 'Subjective/creative requirement requiring human mentor judgment',
  },
  NOT_APPLICABLE: {
    label: 'N/A',
    className: 'text-zinc-400 border-zinc-700 bg-zinc-800/40',
    description: 'Not applicable to this specific submission',
  },
}

/**
 * ProjectStageReviewCard — AI-013 Milestone Review & Delivery Assistant
 *
 * Provides in-workflow deliverable verification and advisory draft feedback
 * for industry mentors and supervising trainers.
 *
 * INVARIANTS:
 * - 0 AI calls on mount (explicit button click required).
 * - Read-only / advisory: AI never approves or requests revisions.
 * - Cardless editorial SMA styling with sharp rectangular geometry.
 */
export default function ProjectStageReviewCard({
  enrollmentId,
  stageIndex,
  apiPrefix = '/project-lab/mentor/enrollments',
  onApplyDraft,
}) {
  const [loading, setLoading] = useState(false)
  const [facts, setFacts] = useState(null)
  const [diagnosis, setDiagnosis] = useState(null)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [hasRequestedAi, setHasRequestedAi] = useState(false)

  // Load deterministic facts on mount (0 LLM calls)
  useEffect(() => {
    if (!enrollmentId || !stageIndex) return
    let isMounted = true

    api
      .get(`${apiPrefix}/${enrollmentId}/stages/${stageIndex}/facts`)
      .then((res) => {
        if (isMounted && res.data?.deterministic_facts) {
          setFacts(res.data.deterministic_facts)
        }
      })
      .catch((err) => {
        // Non-blocking deterministic fact loading
        console.warn('[ProjectStageReviewCard] Facts load error:', err.message)
      })

    return () => {
      isMounted = false
    }
  }, [enrollmentId, stageIndex, apiPrefix])

  // Explicit AI review assist trigger
  const runAiAssist = async () => {
    if (!enrollmentId || !stageIndex) return
    setLoading(true)
    setError(null)
    setHasRequestedAi(true)

    try {
      const res = await api.post(`${apiPrefix}/${enrollmentId}/stages/${stageIndex}/review-assist`)
      if (res.data?.deterministic_facts) {
        setFacts(res.data.deterministic_facts)
      }
      if (res.data?.ai_diagnosis) {
        setDiagnosis(res.data.ai_diagnosis)
      } else if (res.data?.fail_soft) {
        setError(res.data.failure_reason || 'AI review assist temporarily unavailable. Deterministic facts preserved.')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'AI review assist request failed')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyDraft = (text) => {
    if (!text) return
    if (onApplyDraft) {
      onApplyDraft(text)
    }
    navigator.clipboard?.writeText(text).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  const deliveryRisk = facts?.delivery_facts?.ai006_risk_signal

  return (
    <div className="space-y-6 border-t border-white/10 pt-6">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono">
            AI-013 Milestone Review & Delivery Assistant
          </p>
          <h2 className="text-sm font-medium text-white tracking-wide">
            Deliverable Verification & Pacing Snapshot
          </h2>
        </div>

        {!diagnosis && (
          <button
            type="button"
            onClick={runAiAssist}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-cinema-gold/40 text-cinema-gold text-xs uppercase tracking-widest font-mono rounded-none hover:bg-cinema-gold/10 disabled:opacity-40 transition-colors"
          >
            {loading ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Analyzing Deliverables…</span>
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                <span>Analyze Deliverables & Draft Feedback</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* AI-006 Risk Banner (Deterministic) */}
      {deliveryRisk?.triggered && (
        <div className="border border-amber-500/40 bg-amber-500/5 px-4 py-3 space-y-1">
          <div className="flex items-center gap-2 text-amber-300 text-xs font-mono uppercase tracking-wider">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
            <span>AI-006 Delivery Alert — Pacing Risk ({deliveryRisk.severity})</span>
          </div>
          <p className="text-xs text-cinema-soft pl-6">{deliveryRisk.summary}</p>
        </div>
      )}

      {/* Fail-Soft Warning Banner */}
      {error && (
        <div className="border border-amber-500/30 bg-amber-500/5 px-4 py-3 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p className="text-xs text-amber-300 font-mono">Advisory Assist Notice</p>
            <p className="text-xs text-cinema-muted">{error}</p>
          </div>
        </div>
      )}

      {/* Deterministic Milestone Snapshot Grid */}
      {facts && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-3 border-y border-white/5 text-xs font-mono">
          <div>
            <span className="text-[10px] uppercase text-cinema-muted block">Authored Criteria</span>
            <span className="text-white text-sm">
              {facts.stage_facts?.requirements_count || 0} reqs · {facts.stage_facts?.acceptance_criteria_count || 0} criteria
            </span>
          </div>
          <div>
            <span className="text-[10px] uppercase text-cinema-muted block">Files Submitted</span>
            <span className="text-white text-sm">
              {facts.submission_facts?.files_count || 0} files ({(facts.submission_facts?.total_file_size_bytes / (1024 * 1024)).toFixed(1)} MB)
            </span>
          </div>
          <div>
            <span className="text-[10px] uppercase text-cinema-muted block">Participant Notes</span>
            <span className="text-white text-sm">
              {facts.submission_facts?.has_participant_notes ? `${facts.submission_facts?.notes_word_count} words` : 'None'}
            </span>
          </div>
          <div>
            <span className="text-[10px] uppercase text-cinema-muted block">Revisions Requested</span>
            <span className="text-white text-sm">
              {facts.delivery_facts?.revision_count || 0} round{facts.delivery_facts?.revision_count === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      )}

      {/* AI Advisory Diagnosis Section (Only present after explicit invocation) */}
      {diagnosis && (
        <div className="space-y-6 pt-2">
          {/* Executive Summary */}
          {diagnosis.review_summary && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono">
                AI Advisory Review Summary
              </p>
              <p className="text-sm text-cinema-soft leading-relaxed bg-white/[0.02] border border-white/10 p-3">
                {diagnosis.review_summary}
              </p>
            </div>
          )}

          {/* Deliverable Verification Matrix */}
          {diagnosis.deliverable_matrix?.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono">
                  Deliverable Verification Matrix ({diagnosis.deliverable_matrix.length} items)
                </p>
                <span className="text-[10px] font-mono text-cinema-muted">
                  File existence ≠ Requirement fulfillment
                </span>
              </div>

              <div className="border border-white/10 divide-y divide-white/5">
                {diagnosis.deliverable_matrix.map((item, idx) => {
                  const badge = STATUS_BADGES[item.status] || STATUS_BADGES.UNVERIFIABLE
                  return (
                    <div key={item.item_id || idx} className="p-3 space-y-1.5 hover:bg-white/[0.01]">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${badge.className}`}>
                            {badge.label}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-cinema-muted font-mono">
                            {item.item_kind.replace('_', ' ')}
                          </span>
                        </div>
                        {item.evidence && (
                          <span className="text-[11px] text-cinema-muted font-mono truncate max-w-xs">
                            Evidence: {item.evidence}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-white font-medium">{item.criterion}</p>
                      {item.explanation && (
                        <p className="text-xs text-cinema-soft pl-2 border-l border-white/10">
                          {item.explanation}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Strengths and Gaps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Key Strengths */}
            <div className="border border-white/10 p-4 space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono">
                Observable Strengths
              </p>
              {diagnosis.key_strengths?.length > 0 ? (
                <ul className="space-y-2 text-xs">
                  {diagnosis.key_strengths.map((s, idx) => (
                    <li key={idx} className="space-y-0.5">
                      <span className="text-white block">• {s.observation}</span>
                      <span className="text-cinema-muted font-mono text-[10px] block pl-3">
                        Grounding: {s.grounding}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-cinema-muted">No specific strengths highlighted.</p>
              )}
            </div>

            {/* Delivery Gaps */}
            <div className="border border-white/10 p-4 space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-amber-400 font-mono">
                Identified Delivery Gaps
              </p>
              {diagnosis.delivery_gaps?.length > 0 ? (
                <ul className="space-y-2 text-xs">
                  {diagnosis.delivery_gaps.map((g, idx) => (
                    <li key={idx} className="space-y-0.5">
                      <span className="text-white block">• {g.gap}</span>
                      <span className="text-cinema-muted font-mono text-[10px] block pl-3">
                        Action: {g.recommendation}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-cinema-muted">No material gaps detected.</p>
              )}
            </div>
          </div>

          {/* Uncertainties Caveats */}
          {diagnosis.uncertainties?.length > 0 && (
            <div className="border border-white/5 bg-white/[0.01] p-3 space-y-1 text-xs">
              <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono">
                Inspection Caveats & Subjective Areas
              </p>
              <ul className="space-y-1 text-cinema-soft pl-4 list-disc">
                {diagnosis.uncertainties.map((u, idx) => (
                  <li key={idx}>{u}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Draft Feedback Proposal */}
          {diagnosis.draft_feedback && (
            <div className="border border-cinema-gold/30 bg-cinema-gold/[0.02] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-mono text-cinema-gold border border-cinema-gold/40 px-2 py-0.5">
                    AI-PROPOSED / DRAFT (ADVISORY)
                  </span>
                  <span className="text-xs text-cinema-muted font-mono">
                    Editable mentor draft · Not published automatically
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => handleCopyDraft(diagnosis.draft_feedback)}
                  className="flex items-center gap-1.5 px-3 py-1 bg-cinema-gold text-black text-xs uppercase tracking-wider font-mono rounded-none hover:bg-cinema-gold/90 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Copied to Feedback</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy to Review Box</span>
                    </>
                  )}
                </button>
              </div>

              <p className="text-sm text-cinema-soft whitespace-pre-wrap leading-relaxed">
                {diagnosis.draft_feedback}
              </p>
            </div>
          )}

          {/* Re-analyze control */}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={runAiAssist}
              disabled={loading}
              className="text-xs text-cinema-muted hover:text-cinema-gold font-mono uppercase tracking-wider flex items-center gap-1.5"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              <span>Re-analyze Deliverables</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
